import { ArrowLeft, CheckCircle2, Upload as UploadIcon, X } from "lucide-react";
import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PortalShell } from "../components/PortalShell";
import { ApiError, portal } from "../lib/api";

const SOURCE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "bank_statement", label: "Bank statement" },
  { value: "voided_check", label: "Voided check" },
  { value: "government_id", label: "Government ID / driver's license" },
  { value: "sos_document", label: "Business formation / SOS document" },
  { value: "ein_letter", label: "EIN letter" },
  { value: "tax_return", label: "Tax return" },
  { value: "other", label: "Other supporting document" },
];

const MAX_BYTES = 10 * 1024 * 1024;

// One selected letter: the image plus the type the client tags it with. Clients
// can add several at once, each with its own type, then send them all together.
interface PickedLetter {
  id: string;
  file: File;
  type: string;
}

function isLikelyPdf(file: File): boolean {
  const name = file.name.toLowerCase();
  return file.type === "application/pdf" || name.endsWith(".pdf");
}

function validate(f: File): string | null {
  if (!isLikelyPdf(f) && !f.type.startsWith("image/")) {
    return `${f.name}: please upload a PDF or an image (JPG, PNG, HEIC).`;
  }
  if (f.size > MAX_BYTES) {
    return `${f.name}: over 10 MB. Try a smaller file.`;
  }
  return null;
}

export default function PortalUploadPage() {
  const nav = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [letters, setLetters] = useState<PickedLetter[]>([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [doneCount, setDoneCount] = useState(0);
  const [done, setDone] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  function addFiles(list: FileList | null) {
    setError(null);
    if (!list || list.length === 0) return;
    const accepted: PickedLetter[] = [];
    const errors: string[] = [];
    for (const f of Array.from(list)) {
      const err = validate(f);
      if (err) errors.push(err);
      else accepted.push({ id: crypto.randomUUID(), file: f, type: "other" });
    }
    if (accepted.length) setLetters((prev) => [...prev, ...accepted]);
    if (errors.length) setError(errors.join(" "));
  }

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    addFiles(e.target.files);
    e.target.value = "";
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  }

  function setType(id: string, type: string) {
    setLetters((prev) => prev.map((l) => (l.id === id ? { ...l, type } : l)));
  }

  function remove(id: string) {
    setLetters((prev) => prev.filter((l) => l.id !== id));
  }

  async function submit() {
    if (letters.length === 0) {
      setError("Please choose at least one document to upload.");
      return;
    }
    setBusy(true);
    setError(null);
    let uploaded = 0;
    try {
      // Upload each letter with its own type; a shared note applies to all.
      for (const l of letters) {
        try {
          await portal.uploadCorrespondence(l.file, l.type, note.trim() || undefined);
          uploaded++;
        } catch (e: any) {
          if (e instanceof ApiError && e.status === 401) {
            nav("/portal/login", { replace: true });
            return;
          }
          throw new Error(`${l.file.name} didn't upload. ${uploaded} of ${letters.length} sent — try the rest again.`);
        }
      }
      setDoneCount(uploaded);
      setDone(true);
    } catch (e: any) {
      setError(e?.message || "Upload failed. Try again.");
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <PortalShell>
        <section className="portal-section">
          <h1 className="portal-headline">Got it. Thanks.</h1>
        </section>
        <article className="portal-card">
          <div className="portal-confirm">
            <div className="icon" aria-hidden="true">
              <CheckCircle2 size={32} aria-hidden="true" />
            </div>
            <h2 className="portal-headline on-card" style={{ fontSize: 24 }}>
              {doneCount === 1
                ? "Your document is in review."
                : `Your ${doneCount} documents are in review.`}
            </h2>
            <p className="portal-sub on-card" style={{ maxWidth: 460 }}>
              Our team will review {doneCount === 1 ? "it" : "them"} within one business day and update
              your file. You'll see {doneCount === 1 ? "it" : "them"} in your upload history as soon as
              reviewed.
            </p>
            <div
              style={{
                display: "flex",
                gap: 12,
                justifyContent: "center",
                marginTop: 8,
                flexWrap: "wrap",
              }}
            >
              <button
                className="portal-btn-primary"
                onClick={() => {
                  setLetters([]);
                  setNote("");
                  setDoneCount(0);
                  setDone(false);
                }}
              >
                Upload more
              </button>
              <Link className="portal-btn-ghost" to="/portal/dashboard">
                Back to dashboard
              </Link>
            </div>
          </div>
        </article>
      </PortalShell>
    );
  }

  return (
    <PortalShell>
      <section className="portal-section">
        <p className="portal-eyebrow">Upload documents</p>
        <h1 className="portal-headline">Send us your supporting documents.</h1>
        <p className="portal-sub">
          Upload your bank statements, voided check, ID, and business documents and
          we'll add them to your file. You can add several at once — just
          tell us what each one is. <strong style={{ color: "#fff" }}>
            PDF or image — JPG, PNG, or HEIC.
          </strong>{" "}
          Bank statements are usually PDFs — upload them as-is.
        </p>
      </section>

      <article className="portal-card">
        <div className="portal-card-heading">
          <h2>1. Add your documents</h2>
        </div>
        <div
          className="portal-dropzone"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          style={
            dragOver
              ? { borderColor: "var(--ne-red)", background: "var(--ne-red-soft)" }
              : undefined
          }
          role="button"
          tabIndex={0}
          aria-label="Upload your documents. Press Enter or Space to choose files from your device, or drag PDF, JPG, PNG, or HEIC files here. Up to 10 megabytes each. You can add more than one."
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
        >
          <UploadIcon size={28} color="var(--ne-red)" aria-hidden="true" />
          <strong>Drag files here</strong>
          <span>or tap to choose from your device. PDF, JPG, PNG, HEIC — up to 10 MB each. Add as many as you need.</span>
          <input
            ref={inputRef}
            type="file"
            accept="image/*,application/pdf"
            multiple
            onChange={onPick}
          />
        </div>
      </article>

      {letters.length > 0 && (
        <article className="portal-card">
          <div className="portal-card-heading">
            <h2>2. What is each document?</h2>
          </div>
          <div className="portal-section" style={{ gap: 10 }}>
            {letters.map((l, i) => (
              <div
                key={l.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  flexWrap: "wrap",
                  padding: "10px 12px",
                  border: "1px solid rgba(255,255,255,0.14)",
                  borderRadius: 10,
                }}
              >
                <strong className="picked" style={{ flex: "1 1 160px", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {i + 1}. {l.file.name}
                </strong>
                <label className="portal-section" style={{ gap: 4, flex: "0 0 auto" }}>
                  <span className="field-label" style={{ fontSize: 11 }}>Document type</span>
                  <select
                    value={l.type}
                    onChange={(e) => setType(l.id, e.target.value)}
                    aria-label={`Document type for ${l.file.name}`}
                  >
                    {SOURCE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="portal-btn-ghost"
                  onClick={() => remove(l.id)}
                  aria-label={`Remove ${l.file.name}`}
                  style={{ flex: "0 0 auto", padding: "6px 10px" }}
                >
                  <X size={14} aria-hidden="true" /> Remove
                </button>
              </div>
            ))}
          </div>
        </article>
      )}

      <article className="portal-card">
        <div className="portal-card-heading">
          <h2>3. Anything we should know? (optional)</h2>
        </div>
        <textarea
          className="portal-textarea"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          placeholder="A short note to your case team — context, deadlines, anything you want flagged."
        />

        {error && <p className="portal-error">{error}</p>}

        <div
          style={{
            display: "flex",
            gap: 12,
            marginTop: 4,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <button
            className="portal-btn-primary"
            onClick={submit}
            disabled={busy || letters.length === 0}
          >
            {busy
              ? "Uploading…"
              : letters.length > 1
                ? `Send ${letters.length} documents to my funding team`
                : "Send to my funding team"}
          </button>
          <Link className="portal-btn-ghost" to="/portal/dashboard">
            <ArrowLeft size={14} aria-hidden="true" /> Back to dashboard
          </Link>
        </div>
      </article>
    </PortalShell>
  );
}
